/** IVA vigente en Chile. Configurable por tenant; esto es solo el valor inicial. */
export const IVA_PCT_CHILE = 19;
export const MONEDA_POR_DEFECTO = 'CLP';
/**
 * Parámetros por defecto de los motores de predicción.
 *
 * Los tres primeros vienen del CRM original, donde estaban escritos a mano
 * dentro de las funciones: vida media de 8 meses en `pesoRecencia`, factor 0.99
 * sobre el precio y 0.95 sobre el plazo en `triggerPredictor`. Ahora son
 * configurables por tenant desde la UI.
 */
export const PARAMETROS_PREDICTOR_POR_DEFECTO = {
    /** Vida media del decaimiento por recencia, en meses. Más bajo = solo importa lo reciente. */
    vidaMediaMeses: 8,
    /**
     * Multiplicador sobre el precio de mercado estimado. 0.99 = ofertar 1% bajo la
     * media histórica para ser competitivo sin regalar margen.
     */
    agresividadPrecio: 0.99,
    /** Multiplicador sobre el plazo estimado. 0.95 = prometer 5% más rápido. */
    agresividadPlazo: 0.95,
    /**
     * Cuánto pesa extra una licitación histórica del mismo organismo.
     * 1.0 = sin efecto; 2.5 = vale 2,5 veces más que una de otro organismo.
     */
    pesoMismoOrganismo: 2.5,
    /** Peso extra cuando el organismo es distinto pero del mismo tipo (dos SLEP, dos municipios). */
    pesoMismoTipoOrganismo: 1.4,
    /** Peso extra cuando coincide el tipo de proceso (compra ágil vs licitación). */
    pesoMismoTipoProceso: 1.3,
    /**
     * Percentil de winsorización. 10 = los valores bajo el p10 se recortan al p10
     * y los sobre el p90 al p90, antes de promediar. Protege contra el dato raro
     * que con 16 registros secuestraría la recomendación.
     */
    winsorPct: 10,
    /** Margen mínimo aceptable sobre el precio ofertado (%). Bajo esto, el motor alerta. */
    margenMinimoPct: 15,
    /** Plazo mínimo que el motor recomendará jamás, en días. */
    plazoMinimoDias: 3,
};
/**
 * Los 3 Modos Estratégicos Comerciales predefinidos con 1 Clic.
 * Permiten cambiar la postura comercial de la empresa sin tocar código ni fórmulas matemáticas.
 */
export const ESTRATEGIAS_PREDICTOR = {
    AGRESIVO: {
        modo: 'AGRESIVO',
        nombre: 'Modo Agresivo (Captura de Mercado)',
        descripcion: 'Foco: Captura de mercado y penetración. Oferta agresiva al ~82-85% del presupuesto oficial con alta probabilidad de ganar (~85%).',
        probabilidadEstimadaPct: 85,
        ratioObjetivoPct: 83.0,
        parametros: {
            vidaMediaMeses: 6,
            agresividadPrecio: 0.95,
            agresividadPlazo: 0.90,
            pesoMismoOrganismo: 2.5,
            pesoMismoTipoOrganismo: 1.4,
            pesoMismoTipoProceso: 1.3,
            winsorPct: 10,
            margenMinimoPct: 15,
            plazoMinimoDias: 3,
        },
    },
    EQUILIBRADO: {
        modo: 'EQUILIBRADO',
        nombre: 'Modo Equilibrado (Recomendado)',
        descripcion: 'Foco: Balance óptimo entre probabilidad de ganar y rentabilidad saludable. Oferta al ~88% del presupuesto con probabilidad ~72%.',
        probabilidadEstimadaPct: 72,
        ratioObjetivoPct: 88.0,
        parametros: {
            vidaMediaMeses: 8,
            agresividadPrecio: 0.99,
            agresividadPlazo: 0.95,
            pesoMismoOrganismo: 2.5,
            pesoMismoTipoOrganismo: 1.4,
            pesoMismoTipoProceso: 1.3,
            winsorPct: 10,
            margenMinimoPct: 18,
            plazoMinimoDias: 3,
        },
    },
    CONSERVADOR: {
        modo: 'CONSERVADOR',
        nombre: 'Modo Rentabilidad / Cosecha',
        descripcion: 'Foco: Margen alto y seguro. Para empresas con capacidad casi llena. Oferta al ~94% del presupuesto con probabilidad ~45%.',
        probabilidadEstimadaPct: 45,
        ratioObjetivoPct: 94.0,
        parametros: {
            vidaMediaMeses: 12,
            agresividadPrecio: 1.03,
            agresividadPlazo: 1.00,
            pesoMismoOrganismo: 2.5,
            pesoMismoTipoOrganismo: 1.4,
            pesoMismoTipoProceso: 1.3,
            winsorPct: 10,
            margenMinimoPct: 25,
            plazoMinimoDias: 5,
        },
    },
};
/**
 * Umbrales de tamaño efectivo de muestra (n_eff de Kish) para clasificar la
 * confianza de una predicción.
 *
 * El CRM original usaba umbrales sobre la suma cruda de pesos, que no es
 * comparable entre categorías con distinta cantidad de datos. n_eff sí lo es:
 * responde "¿a cuántas observaciones equivale realmente esta muestra ponderada?".
 */
export const UMBRALES_CONFIANZA = {
    alta: 4,
    media: 2,
};
/** Categorías de gasto iniciales. Vienen de `GASTO_CATEGORIAS` del CRM original. */
export const CATEGORIAS_GASTO_INICIALES = [
    { nombre: 'Personal', tipo: 'DIRECTO', esCostoPersonal: true },
    { nombre: 'Infraestructura/Hosting', tipo: 'DIRECTO', esCostoPersonal: false },
    { nombre: 'Viáticos', tipo: 'DIRECTO', esCostoPersonal: false },
    { nombre: 'Subcontratos', tipo: 'DIRECTO', esCostoPersonal: false },
    { nombre: 'Licencias/Software', tipo: 'DIRECTO', esCostoPersonal: false },
    { nombre: 'Marketing', tipo: 'INDIRECTO', esCostoPersonal: false },
    { nombre: 'Administración', tipo: 'INDIRECTO', esCostoPersonal: false },
    { nombre: 'Otros', tipo: 'DIRECTO', esCostoPersonal: false },
];
/**
 * Categorías comerciales iniciales, con el color del diseño original y un margen
 * objetivo por línea de negocio.
 *
 * Los márgenes reflejan la realidad del rubro: reventa de hardware (Compra/Venta)
 * deja poco, el software propio deja mucho más.
 */
export const CATEGORIAS_INICIALES = [
    { nombre: 'Software', color: '#2196F3', margenObjetivoPct: 45 },
    { nombre: 'Web', color: '#00C853', margenObjetivoPct: 50 },
    { nombre: 'Inventario', color: '#FFC107', margenObjetivoPct: 35 },
    { nombre: 'Servicios', color: '#9C27B0', margenObjetivoPct: 40 },
    { nombre: 'Compra/Venta', color: '#78909C', margenObjetivoPct: 15 },
    { nombre: 'Consultoría', color: '#FF7043', margenObjetivoPct: 50 },
    { nombre: 'IA', color: '#D32F2F', margenObjetivoPct: 55 },
];
/** Ítems de checklist de cierre que aplican a casi todo proyecto público chileno. */
export const CHECKLIST_CIERRE_SUGERIDO = [
    'Acta de Recepción Conforme firmada',
    'Entrega de documentación técnica al organismo',
    'Factura emitida y enviada',
    'Pago recibido en su totalidad',
    'Respaldo de entregables archivado',
];
export const CONDICION_PAGO_POR_DEFECTO = 'DIAS_30';
export const PERIODICIDAD_POR_DEFECTO = 'MENSUAL';
/**
 * Tramos de antigüedad de la cartera por cobrar, en días vencidos.
 * `hasta: null` es el tramo abierto final.
 */
export const TRAMOS_ANTIGUEDAD = [
    { clave: 'POR_VENCER', etiqueta: 'Por vencer', desde: -Infinity, hasta: 0 },
    { clave: 'D_0_30', etiqueta: '1 a 30 días', desde: 1, hasta: 30 },
    { clave: 'D_31_60', etiqueta: '31 a 60 días', desde: 31, hasta: 60 },
    { clave: 'D_61_90', etiqueta: '61 a 90 días', desde: 61, hasta: 90 },
    { clave: 'D_90_MAS', etiqueta: 'Más de 90 días', desde: 91, hasta: null },
];
/** Días de anticipación con que el sistema avisa de cada tipo de evento. */
export const DIAS_ANTICIPACION_ALERTA = {
    plazoPostulacion: 7,
    finProyecto: 30,
    vencimientoFactura: 7,
    hitoPlanificado: 7,
    /** Días sin respuesta tras cerrar la postulación antes de avisar que la oferta está estancada. */
    ofertaSinRespuesta: 45,
};
/** Umbrales de consumo de presupuesto de costos que disparan alerta (%). */
export const UMBRALES_PRESUPUESTO = {
    advertencia: 80,
    excedido: 100,
};
//# sourceMappingURL=constantes.js.map